import styled from 'styled-components';

import { BREAKPOINTS } from '@shared/styled-system';

export const RaceCard = styled.div<{ $gradient?: string; $isCompleted?: boolean; $isPending?: boolean, $isJackpot?: boolean }>`
  background: ${({ $gradient, $isCompleted, $isPending, $isJackpot, theme }) => {
    if ($isCompleted || ($isPending && !$isJackpot)) {
      return theme.colors.background.secondary;
    }
    
    if ($isJackpot || ($isPending && $isJackpot)) {
      return "radial-gradient(68.68% 69.2% at 88.09% 100%, rgba(9, 142, 160, 0.60) 0%, rgba(16, 65, 71, 0.60) 50%, rgba(0, 0, 0, 0.00) 100%), radial-gradient(79.54% 119.3% at 0% 100%, rgba(69, 139, 254, 0.60) 0%, rgba(44, 78, 250, 0.60) 30%, rgba(28, 41, 117, 0.60) 60%, rgba(0, 0, 0, 0.00) 100%), #060606"
    }
    
    return $gradient || 'linear-gradient(45deg, rgba(6, 6, 6, 1) 0%, rgba(0, 71, 187, 1) 100%)';
  }};
  
  border-radius: 4px;
  padding: 12px;
  transition: transform 0.2s;
  display: flex;
  flex-direction: column;
  position: relative;

  &:hover {
    transform: translateY(-4px);
  }

  @media (${BREAKPOINTS.MOBILE}) {
    padding: 8px;
  }
`;

export const StatusBadge = styled.div<{
	$status: 'active' | 'pending' | 'completed';
	$isRelative?: boolean;
	$media: 'mobile' | 'desktop';
}>`
	padding: 4px 6px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
	text-transform: capitalize;

	${({ $isRelative }) =>
		$isRelative
			? `
        position: relative;
      `
			: `
        position: absolute;
        top: 12px;
        right: 12px;
      `}

	${({ $status, theme }) => {
		if ($status === 'active') {
			return `
        color: #47D7AC;
        background-color: rgba(71, 215, 172, 0.1);
      `;
		} else if ($status === 'pending') {
			return `
        color: ${theme.colors.text.alwaysWhite};
        background-color: ${theme.colors.transparent.light10};
      `;
		} else {
			return `
        color: #FBAE27;
        background-color: rgba(251, 174, 39, 0.1);
      `;
		}
	}}
	
	@media (${BREAKPOINTS.DESKTOP}) {
		${({ $media }) => $media === 'mobile' && 'display: none;'}
	}

	@media (${BREAKPOINTS.NOT_DESKTOP}) {
		${({ $media }) => $media === 'desktop' && 'display: none;'}
	}
`;

export const DateBadge = styled.div`
	position: absolute;
	top: 16px;
	left: 12px;
	right: unset;
	font-size: 14px;
	z-index: 1;
	color: ${({ theme }) => theme.colors.text.alwaysWhite};

	@media (${BREAKPOINTS.NOT_DESKTOP}) {
		top: 8px;
		right: 8px;
		left: unset;
		font-size: 10px;
		font-weight: 500;
	}
`;

export const RaceImage = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;

	img {
		max-height: 99px;
		object-fit: contain;
		filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
	}
`;

export const RaceInfo = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 85px;
	margin-bottom: 8px;
	gap: 8px;

	@media (${BREAKPOINTS.NOT_DESKTOP}) {
		min-height: 109px;
	}
`;

export const RaceName = styled.h3`
	font-size: 16px;
	font-weight: 500;
	color: ${({ theme }) => theme.colors.text.title};
	margin: 0;
	text-align: center;
`;

export const JackpotLabel = styled.div<{ $alignCenter?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: ${({ $alignCenter }) => ($alignCenter ? 'center' : 'space-between')};
	width: 100%;

	svg {
		fill: ${({ theme }) => theme.colors.icon.chevron};
	}

	@media (${BREAKPOINTS.MOBILE}) {
		display: none;
	}
`;

export const JackpotLabelInfoIcon = styled.div`
	display: flex;
	align-items: center;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.7;
	}
`;

export const JackpotLabelInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	color: ${({ theme }) => theme.colors.text.body};
	line-height: 0;
`;

export const JackpotText = styled.span`
	@media (${BREAKPOINTS.MOBILE}) {
		display: none;
	}
`;

export const JackpotValue = styled.div`
	font-size: 20px;
	font-weight: 700;
	color: ${({ theme }) => theme.colors.text.alwaysWhite};
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
`;

export const JackpotIcon = styled.span`
	display: none;

	svg {
		fill: ${({ theme }) => theme.colors.icon.chevron};
	}

	@media (${BREAKPOINTS.MOBILE}) {
		display: flex;
		align-items: center;
	}
`;

export const RaceStats = styled.div<{ $isPending?: boolean }>`
	background-color: ${({ theme }) => theme.colors.transparent.light5};
	border-radius: 4px;
	display: grid;
	grid-template-columns: ${({ $isPending }) => ($isPending ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)')};
	height: 54px;
	margin-bottom: 8px;
	padding: 8px;

	${({ $isPending }) =>
		$isPending
			? `
    & > :first-child {
      border-right: 1px solid rgba(255, 255, 255, 0.05);
    }
  `
			: `
    & > :nth-child(2) {
      border-left: 1px solid rgba(255, 255, 255, 0.05);
      border-right: 1px solid rgba(255, 255, 255, 0.05);
    }
  `}
`;

export const RaceStat = styled.div<{ $iconColor?: string }>`
	align-items: center;
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 4px;
	font-size: 13px;
	font-weight: 500;
	color: ${({ theme }) => theme.colors.text.alwaysWhite};

	svg {
		width: 18px;
		height: 18px;
		flex-shrink: 0;
		fill: ${({ $iconColor, theme }) => $iconColor || theme.colors.icon.chevron};
	}
`;

export const RaceActions = styled.div`
	display: flex;
	gap: 8px;
	justify-content: flex-end;
`;
